import { useQuery, useQueryClient } from '@tanstack/react-query';

import { RequestConfig } from '~/common/services/requests';
import { AnyDocument } from '../../../../../types';
import { useRequestConstructor } from '../../../../hooks/useRequestConstructor';
import { unwrapPaginatedResponseDataAs } from '../../../../logic/unwrapPaginatedResponseDataAs';
import { NonPaginatedResponse } from '../../../../types/NonPaginatedResponse';
import { APPLICATION_BASE_PATH } from '../../constants/applicationBasePath';
import { getApplicationQueryKey } from '../../logic/getApplicationQueryKey';
import {
  getApplications,
  GetApplicationsHeaders,
} from '../../requests/getApplications';

export const useApplications = (
  params?: RequestConfig['params'],
  headers?: GetApplicationsHeaders,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
  },
) => {
  const { constructGet } = useRequestConstructor();
  const queryClient = useQueryClient();

  const { data, refetch, ...rest } = useQuery<
    Awaited<ReturnType<typeof getApplications>>
  >({
    queryKey: [APPLICATION_BASE_PATH, headers?.definitionName, params, headers],
    queryFn: () => getApplications(constructGet, params, headers),
    enabled: options?.enabled ?? true,
    keepPreviousData: options?.keepPreviousData ?? true,
    staleTime: options?.staleTime ?? 10000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    onSuccess: (response) => {
      response?.data?.forEach((document) => {
        queryClient.setQueryData<NonPaginatedResponse<AnyDocument>>(
          getApplicationQueryKey(document.id, {
            definition: headers?.definitionName,
            workflow: headers?.workflowName,
            statusDefinition: headers?.statusDefinition,
          }),
          { data: document },
        );
      });
    },
  });

  return {
    ...unwrapPaginatedResponseDataAs(data, refetch, 'applications'),
    ...rest,
  };
};
